import React, { useMemo, useState, useCallback } from 'react'
import Select, { components } from 'react-select'
import { StylesConfig } from 'react-select'
import { useSelector } from 'react-redux'
import { IoMdArrowDropright, IoMdArrowDropdown } from 'react-icons/io'

interface Department {
  id: number
  name: string
  parent: {
    id: number
    name: string
  } | null
  created_at: string
  updated_at: string
}

interface TreeNode {
  id: number
  name: string
  children: TreeNode[]
  parent: Department['parent']
}

// Helper function to build tree from flat array
const buildTree = (departments: Department[]): TreeNode[] => {
  const map = new Map<number, TreeNode>()
  const tree: TreeNode[] = []

  // First pass: Create nodes for all departments
  departments.forEach((dept) => {
    if (!map.has(dept.id)) {
      map.set(dept.id, {
        id: dept.id,
        name: dept.name,
        children: [],
        parent: dept.parent,
      })
    }
  })

  // Second pass: Build the tree structure
  departments.forEach((dept) => {
    const node = map.get(dept.id)!
    if (dept.parent) {
      const parentNode = map.get(dept.parent.id)
      if (parentNode) {
        parentNode.children.push(node)
      }
    } else {
      tree.push(node)
    }
  })

  return tree
}

interface FlattenedOption {
  value: number
  label: string
  level: number
  hasChildren: boolean
  parentId: number | null
  isVisible: boolean
}

const flattenTreeWithParentInfo = (
  nodes: TreeNode[],
  level = 0,
  parentId: number | null = null
): FlattenedOption[] => {
  let options: FlattenedOption[] = []

  nodes.forEach((node) => {
    options.push({
      value: node.id,
      label: node.name,
      level,
      hasChildren: node.children && node.children.length > 0,
      parentId,
      isVisible: level === 0,
    })

    if (node.children && node.children.length > 0) {
      options = [
        ...options,
        ...flattenTreeWithParentInfo(node.children, level + 1, node.id),
      ]
    }
  })

  return options
}

interface HierarchicalSelectProps {
  label: string
  value: any
  treeData: Department[]
  onChange: (selectedOption: any) => void
  placeholder: string
  disabled?: boolean
  height?: string
  marginTop?: number
  isRequired?: boolean
}

const HierarchicalSelect: React.FC<HierarchicalSelectProps> = ({
  label,
  value,
  treeData,
  onChange,
  placeholder,
  disabled,
  height,
  marginTop = 10,
  isRequired = false,
}) => {
  const { userCompany } = useSelector((state: any) => state.auth)
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set())
  const [allOptions, setAllOptions] = useState<FlattenedOption[]>([])

  // Convert tree data to flat options with level information
  useMemo(() => {
    const tree = buildTree(treeData || [])
    const flatOptions = flattenTreeWithParentInfo(tree)
    setAllOptions(flatOptions)
  }, [treeData])

  // Filter options based on which nodes are expanded
  const visibleOptions = useMemo(() => {
    return allOptions.filter((option) => {
      if (option.level === 0) return true

      if (option.parentId && expandedNodes.has(option.parentId)) {
        let currentOption = option
        let isVisible = true

        while (currentOption.parentId && isVisible) {
          const parent = allOptions.find(
            (o) => o.value === currentOption.parentId
          )
          if (!parent || !expandedNodes.has(parent.value)) {
            isVisible = false
          }
          currentOption = parent as any
        }

        return isVisible
      }
      return false
    })
  }, [allOptions, expandedNodes])

  const toggleNode = useCallback((nodeId: number) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId)
      } else {
        newSet.add(nodeId)
      }
      return newSet
    })
  }, [])

  const CustomOption = useCallback(
    (props: any) => {
      const { data } = props
      const isExpanded = expandedNodes.has(data.value)

      const handleToggle = (e: React.MouseEvent) => {
        if (data.hasChildren) {
          e.preventDefault()
          e.stopPropagation()
          toggleNode(data.value)
        }
      }

      return (
        <components.Option {...props}>
          <div
            style={{
              paddingLeft: `${data.level * 20}px`,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'transparent',
            }}
          >
            {data.hasChildren ? (
              <span
                onClick={handleToggle}
                style={{
                  marginRight: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  width: '20px',
                  height: '20px',
                }}
              >
                {isExpanded ? (
                  <IoMdArrowDropdown size={15} />
                ) : (
                  <IoMdArrowDropright size={15} />
                )}
              </span>
            ) : (
              <span style={{ width: '16px', marginRight: '8px' }}></span>
            )}
            {props.children}
          </div>
        </components.Option>
      )
    },
    [expandedNodes, toggleNode]
  )

  const customStyles: StylesConfig = {
    control: (provided, state) => ({
      ...provided,
      textAlign: 'left',
      fontWeight: 'normal',
      fontSize: 14,
      borderRadius: 8,
      minHeight: height || provided.height,
      borderColor: state.isFocused
        ? userCompany?.button_color
        : provided.borderColor,
      boxShadow: state.isFocused
        ? `0 0 0 1px ${userCompany?.button_color}`
        : provided.boxShadow,
      '&:hover': {
        borderColor: userCompany?.button_color,
      },
      cursor: 'pointer',
    }),
    option: (provided) => ({
      ...provided,
      textAlign: 'left',
      fontWeight: 'normal',
      fontSize: 12,
      '&:active': {
        backgroundColor: 'rgba(67, 56, 202, 0.1) !important',
      },
      cursor: 'pointer',
    }),
    menu: (provided) => ({
      ...provided,
      overflowY: 'auto',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 175,
      '::-webkit-scrollbar': {
        width: '10px',
        height: '2px',
      },
      '::-webkit-scrollbar-track': {
        background: 'lightGrey',
        borderRadius: '8px',
      },
      '::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '8px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: '#555',
        borderRadius: '8px',
      },
    }),
  }

  const renderLabel = () => {
    if (typeof label === 'string') {
      return (
        <span>
          {label}
          {isRequired && (
            <span
              style={{
                color: userCompany?.button_color || '#ff69b4',
                marginLeft: '3px',
              }}
            >
              *
            </span>
          )}
        </span>
      )
    }
    return label
  }

  return (
    <div>
      <label
        htmlFor="hierarchical-select"
        style={{
          textAlign: 'left',
          marginTop: marginTop,
          marginBottom: '0.5rem',
          fontSize: '16px',
          fontWeight: 'bold',
          color: 'black',
          display: 'inline-block',
        }}
      >
        {renderLabel()}
      </label>
      <Select
        inputId="hierarchical-select"
        value={value}
        options={visibleOptions}
        onChange={onChange}
        placeholder={placeholder}
        isSearchable
        styles={customStyles}
        components={{ Option: CustomOption }}
        aria-label={typeof label === 'string' ? label : 'dropdown'}
        isDisabled={disabled}
        aria-required={isRequired}
      />
    </div>
  )
}

export default HierarchicalSelect
